<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import InstagramCommentAutomationsAPI from 'dashboard/api/instagramCommentAutomations';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const automations = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const dialogRef = ref(null);
const editingId = ref(null);

const createEmptyForm = () => ({
  name: '',
  keywords: '',
  publicReplyEnabled: true,
  privateReplyEnabled: true,
  publicReplyText: '',
  privateReplyText: '',
  active: true,
});

const form = reactive(createEmptyForm());

const dialogTitle = computed(() =>
  editingId.value
    ? t('INSTAGRAM_COMMENT_AUTOMATION.EDIT_TITLE')
    : t('INSTAGRAM_COMMENT_AUTOMATION.CREATE_TITLE')
);

const isFormInvalid = computed(() => {
  if (!form.name.trim()) return true;
  if (!form.publicReplyEnabled && !form.privateReplyEnabled) return true;
  if (form.publicReplyEnabled && !form.publicReplyText.trim()) return true;
  if (form.privateReplyEnabled && !form.privateReplyText.trim()) return true;
  return false;
});

const loadAutomations = async () => {
  isLoading.value = true;
  try {
    const { data } = await InstagramCommentAutomationsAPI.get(props.inboxId);
    automations.value = data;
  } catch (error) {
    useAlert(t('INSTAGRAM_COMMENT_AUTOMATION.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  editingId.value = null;
  Object.assign(form, createEmptyForm());
};

const openCreate = () => {
  resetForm();
  dialogRef.value?.open();
};

const openEdit = automation => {
  editingId.value = automation.id;
  Object.assign(form, {
    name: automation.name,
    keywords: (automation.keywords || []).join(', '),
    publicReplyEnabled: automation.public_reply_enabled,
    privateReplyEnabled: automation.private_reply_enabled,
    publicReplyText: automation.public_reply_text || '',
    privateReplyText: automation.private_reply_text || '',
    active: automation.active,
  });
  dialogRef.value?.open();
};

const payload = () => ({
  instagram_comment_automation: {
    name: form.name.trim(),
    keywords: form.keywords
      .split(',')
      .map(keyword => keyword.trim())
      .filter(Boolean),
    public_reply_enabled: form.publicReplyEnabled,
    private_reply_enabled: form.privateReplyEnabled,
    public_reply_text: form.publicReplyText.trim(),
    private_reply_text: form.privateReplyText.trim(),
    active: form.active,
  },
});

const saveAutomation = async () => {
  if (isFormInvalid.value) return;

  isSaving.value = true;
  try {
    if (editingId.value) {
      const { data } = await InstagramCommentAutomationsAPI.update(
        props.inboxId,
        editingId.value,
        payload()
      );
      automations.value = automations.value.map(automation =>
        automation.id === data.id ? data : automation
      );
    } else {
      const { data } = await InstagramCommentAutomationsAPI.create(
        props.inboxId,
        payload()
      );
      automations.value = [...automations.value, data];
    }
    useAlert(t('INSTAGRAM_COMMENT_AUTOMATION.API.SAVE_SUCCESS'));
    dialogRef.value?.close();
    resetForm();
  } catch (error) {
    useAlert(t('INSTAGRAM_COMMENT_AUTOMATION.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deleteAutomation = async automation => {
  try {
    await InstagramCommentAutomationsAPI.delete(props.inboxId, automation.id);
    automations.value = automations.value.filter(
      item => item.id !== automation.id
    );
  } catch (error) {
    useAlert(t('INSTAGRAM_COMMENT_AUTOMATION.API.DELETE_ERROR'));
  }
};

const toggleAutomation = async automation => {
  try {
    const { data } = await InstagramCommentAutomationsAPI.update(
      props.inboxId,
      automation.id,
      {
        instagram_comment_automation: { active: !automation.active },
      }
    );
    automations.value = automations.value.map(item =>
      item.id === data.id ? data : item
    );
  } catch (error) {
    useAlert(t('INSTAGRAM_COMMENT_AUTOMATION.API.SAVE_ERROR'));
  }
};

onMounted(loadAutomations);
</script>

<template>
  <section class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h3 class="mb-1 text-base font-medium text-n-slate-12">
          {{ t('INSTAGRAM_COMMENT_AUTOMATION.TITLE') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('INSTAGRAM_COMMENT_AUTOMATION.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-plus"
        :label="t('INSTAGRAM_COMMENT_AUTOMATION.CREATE')"
        @click="openCreate"
      />
    </div>

    <p v-if="isLoading" class="mt-4 mb-0 text-sm text-n-slate-11">
      {{ t('INSTAGRAM_COMMENT_AUTOMATION.LOADING') }}
    </p>
    <p
      v-else-if="!automations.length"
      class="mt-4 mb-0 text-sm text-n-slate-11"
    >
      {{ t('INSTAGRAM_COMMENT_AUTOMATION.EMPTY') }}
    </p>
    <div v-else class="mt-4 flex flex-col gap-3">
      <div
        v-for="automation in automations"
        :key="automation.id"
        class="flex items-start justify-between gap-4 p-3 border rounded-lg border-n-weak"
      >
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <p class="mb-0 font-medium text-n-slate-12">
              {{ automation.name }}
            </p>
            <span class="text-xs text-n-slate-10">
              {{
                automation.keywords.length
                  ? automation.keywords.join(', ')
                  : t('INSTAGRAM_COMMENT_AUTOMATION.ALL_COMMENTS')
              }}
            </span>
          </div>
          <p class="mt-1 mb-0 text-xs text-n-slate-11">
            {{
              [
                automation.public_reply_enabled
                  ? t('INSTAGRAM_COMMENT_AUTOMATION.PUBLIC_REPLY')
                  : null,
                automation.private_reply_enabled
                  ? t('INSTAGRAM_COMMENT_AUTOMATION.PRIVATE_REPLY')
                  : null,
              ]
                .filter(Boolean)
                .join(' + ')
            }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Switch
            :model-value="automation.active"
            @change="toggleAutomation(automation)"
          />
          <Button
            icon="i-lucide-pencil"
            slate
            faded
            sm
            @click="openEdit(automation)"
          />
          <Button
            icon="i-lucide-trash"
            ruby
            faded
            sm
            @click="deleteAutomation(automation)"
          />
        </div>
      </div>
    </div>
  </section>

  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :title="dialogTitle"
    :description="t('INSTAGRAM_COMMENT_AUTOMATION.FORM_DESCRIPTION')"
    :confirm-button-label="t('INSTAGRAM_COMMENT_AUTOMATION.SAVE')"
    :disable-confirm-button="isFormInvalid"
    :is-loading="isSaving"
    @confirm="saveAutomation"
    @close="resetForm"
  >
    <Input
      v-model="form.name"
      :label="t('INSTAGRAM_COMMENT_AUTOMATION.FORM.NAME')"
      :placeholder="t('INSTAGRAM_COMMENT_AUTOMATION.FORM.NAME_PLACEHOLDER')"
    />
    <Input
      v-model="form.keywords"
      :label="t('INSTAGRAM_COMMENT_AUTOMATION.FORM.KEYWORDS')"
      :placeholder="t('INSTAGRAM_COMMENT_AUTOMATION.FORM.KEYWORDS_PLACEHOLDER')"
    />
    <div class="flex items-center justify-between gap-4">
      <span class="text-sm text-n-slate-12">
        {{ t('INSTAGRAM_COMMENT_AUTOMATION.FORM.PUBLIC_REPLY_ENABLED') }}
      </span>
      <Switch v-model="form.publicReplyEnabled" />
    </div>
    <TextArea
      v-if="form.publicReplyEnabled"
      v-model="form.publicReplyText"
      auto-height
      :label="t('INSTAGRAM_COMMENT_AUTOMATION.FORM.PUBLIC_REPLY_TEXT')"
      :placeholder="
        t('INSTAGRAM_COMMENT_AUTOMATION.FORM.PUBLIC_REPLY_PLACEHOLDER')
      "
    />
    <div class="flex items-center justify-between gap-4">
      <span class="text-sm text-n-slate-12">
        {{ t('INSTAGRAM_COMMENT_AUTOMATION.FORM.PRIVATE_REPLY_ENABLED') }}
      </span>
      <Switch v-model="form.privateReplyEnabled" />
    </div>
    <TextArea
      v-if="form.privateReplyEnabled"
      v-model="form.privateReplyText"
      auto-height
      :label="t('INSTAGRAM_COMMENT_AUTOMATION.FORM.PRIVATE_REPLY_TEXT')"
      :placeholder="
        t('INSTAGRAM_COMMENT_AUTOMATION.FORM.PRIVATE_REPLY_PLACEHOLDER')
      "
    />
    <p class="mb-0 text-xs text-n-slate-11">
      {{ t('INSTAGRAM_COMMENT_AUTOMATION.FORM.VARIABLES_HINT') }}
    </p>
  </Dialog>
</template>
